/**
 * @fileoverview Utilities for extracting encrypted document paths from upload responses.
 *
 * This module provides functionality to navigate complex nested response structures
 * and locate specific documents based on multiple criteria including applicant ID,
 * document type, file name, size, and category.
 */

export interface IDocumentType {
    documentType: string;
    uploadedFileName?: string;
    uploadedFileSize?: number | string;
    encryptedDocumentPath?: string;
}

export interface IDocument {
    categoryType?: string;
    documentTypes?: IDocumentType[];
}

export interface IApplicant {
    applicantEventId: number | string;
    documents?: IDocument[];
}

export interface IUploadResponsePayload {
    applicants?: IApplicant[];
}

export interface IUploadResponse {
    payload?: IUploadResponsePayload;
    success?: boolean;
    message?: string;
}

export interface IFindEncryptedDocumentPathParams {
    res: IUploadResponse;
    applicantEventId: number | string;
    documentType: string;
    uploadedFileName: string;
    uploadedFileSize?: number;
    categoryType?: string;
}

/**
 * Extracts the encrypted document path from the upload response.
 *
 * Navigates the nested response structure to find the document that matches:
 * - applicantEventId
 * - documentType
 * - uploadedFileName (optional)
 * - uploadedFileSize (optional)
 * - categoryType (optional)
 *
 * @param params - Parameters for finding the document path
 * @param params.res - The upload response containing applicants and documents
 * @param params.applicantEventId - The applicant event ID to match
 * @param params.documentType - The document type to match
 * @param params.uploadedFileName - Optional file name to filter by
 * @param params.uploadedFileSize - Optional file size to filter by
 * @param params.categoryType - Optional category type to filter by
 * @returns The encrypted document path, or null if not found
 *
 * @remarks
 * If multiple documents match all criteria, the last one in the array is selected.
 * This is intentional to handle cases where the same document might be uploaded multiple times.
 */

export const findEncryptedDocumentPath = ({
    res,
    applicantEventId,
    documentType,
    uploadedFileName,
    uploadedFileSize,
    categoryType,
}: IFindEncryptedDocumentPathParams): string | null => {
    const applicants = res?.payload?.applicants ?? [];

    const applicant = applicants.find(a => Number(a.applicantEventId) === Number(applicantEventId));

    const documents = applicant?.documents ?? [];

    const scopedDocs = categoryType ? documents.filter(d => d.categoryType === categoryType) : documents;
    const allDocTypes = scopedDocs.flatMap(d => d.documentTypes ?? []);
    let candidates = allDocTypes.filter(dt => dt.documentType === documentType);

    if (uploadedFileName) {
        candidates = candidates.filter(dt => dt.uploadedFileName === uploadedFileName);
    }
    if (uploadedFileSize !== null) {
        candidates = candidates.filter(dt => Number(dt.uploadedFileSize) === Number(uploadedFileSize));
    }

    if (candidates.length > 1 && process.env.NODE_ENV === 'development') {
        console.warn('Multiple documents match criteria. Selecting last one.', {
            candidates,
            documentType,
            uploadedFileName,
            uploadedFileSize,
            categoryType,
        });
    }

    const picked = candidates.length ? candidates[candidates.length - 1] : null;

    return picked?.encryptedDocumentPath || null;
};
